/**
 * A utility class for performing common DOM-related operations.
 */
export class DomHelper {

    /**
     * Retrieves the computed style value of a specified CSS property for a given HTML element.
     *
     * @param {HTMLElement} element - The HTML element for which the computed style is to be retrieved.
     * @param {string} propertyName - The name of the CSS property whose computed value is to be retrieved.
     * @return {string} The computed value of the specified CSS property for the given element.
     */
    static getComputedStyle(element: HTMLElement, propertyName: string): string {
        const style = window.getComputedStyle(element);
        return style.getPropertyValue(propertyName);
    }

    /**
     * Retrieves the computed value of a CSS custom property (variable) from a given HTML element.
     *
     * @param {HTMLElement} element - The HTML element from which to retrieve the computed CSS variable value.
     * @param {string} variableName - The name of the CSS variable (excluding the leading '--') to retrieve the value for.
     * @return {string} The computed value of the specified CSS variable as a string.
     */
    static getComputedVariableValue(element: HTMLElement, variableName: string): string {
        return DomHelper.getComputedStyle(element, `--${variableName}`);
    }

    /**
     * Sets a CSS custom property (CSS variable) on the specified HTML element.
     *
     * @param {HTMLElement} element - The HTML element on which the CSS variable will be set.
     * @param {string} variableName - The name of the CSS variable (without the "--" prefix).
     * @param {string} value - The value to assign to the CSS variable.
     * @return {void} This method does not return a value.
     */
    static setVariableValue(element: HTMLElement, variableName: string, value: string): void {
        DomHelper.setProperty(element, `--${variableName}`, value);
    }

    /**
     * Sets a CSS property on a specified HTML element.
     *
     * @param {HTMLElement} element - The HTML element on which the CSS property will be set.
     * @param {string} propertyName - The name of the CSS property to set.
     * @param {string} value - The value to assign to the specified CSS property.
     * @return {void} This method does not return a value.
     */
    static setProperty(element: HTMLElement, propertyName: string, value: string): void {
        element.style.setProperty(propertyName, value);
    }
}